import React, { useMemo } from "react";
import dayjs from "dayjs";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Trans } from "@coworker/locales";
import {
  getActiveTask,
  calculateNumberOfVotes,
} from "./QuestionsDetailPage.helper";
import { Question } from "../BackendApiRepository/QuestionResponse";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { usePendingMfaqTasks, mfaqTaskKey } from "../hooks/usePendingMfaqTasks";

interface MfaqTaskButtonProps {
  question: Question;
  filters: any; //TODO: Find out if we have type for this
  periodStartDate?: string;
  periodEndDate?: string;
}

const MfaqTaskButtonComponent: React.FC<MfaqTaskButtonProps> = ({
  question,
  filters,
  periodStartDate,
  periodEndDate,
}) => {
  const navigate = useFixaNavigate();
  const { toggleModal } = React.useContext(ModalContext);

  const enableMfaqV2 = useFlag(FLAGS.ENABLE_MFAQ_FOLLOWUP_TASK_V2);
  const [, setSearchParams] = useSearchParamState();
  const { data: taskTypes } = useAllTaskTypes();
  const mfaqFollowUpId = useMemo(
    () =>
      taskTypes?.find(({ name }) => name === TaskTypeNames.MFAQ_FOLLOW_UP)?.id,
    [taskTypes]
  );

  // v2: look up the pending task directly from tasks-v2 by (question_id, area).
  const { pendingTaskByKey } = usePendingMfaqTasks({ enabled: enableMfaqV2 });
  const v2PendingTask = enableMfaqV2
    ? (pendingTaskByKey.get(
        mfaqTaskKey(question.question_id, filters[1] ?? "")
      ) ?? null)
    : null;

  // v1: read from the task list embedded in the question object by the MFAQ service.
  const { hasOngoingTask: v1HasOngoingTask, taskId: v1TaskId } = getActiveTask(
    question,
    filters[1]
  );

  const hasOngoingTask = enableMfaqV2 ? !!v2PendingTask : v1HasOngoingTask;
  const taskId = enableMfaqV2 ? (v2PendingTask?.id ?? null) : v1TaskId;
  const taskString = hasOngoingTask ? "showTaskString" : "createTaskString";

  const createMFAQTask = () => {
    if (enableMfaqV2 && mfaqFollowUpId) {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(mfaqFollowUpId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              question_text: question.question_text,
              question_id: question.question_id,
              question_area: filters[1] ?? "",
              upvotes: calculateNumberOfVotes(question, filters[1] ?? "") ?? 0,
              upvotes_period:
                filters[0] === "period=custom" &&
                periodStartDate &&
                periodEndDate
                  ? `${dayjs(periodStartDate).format("D MMM YYYY")} – ${dayjs(periodEndDate).format("D MMM YYYY")}`
                  : (filters[0] ?? ""),
            },
          })
        );
      });
    } else {
      navigate(`/task/new/mfaq_followup`, {
        state: {
          question: { ...question, question_current_filters: filters },
        },
      });
    }
  };
  const showMFAQTask = () => {
    if (enableMfaqV2) {
      setSearchParams((prev) => {
        prev.set("taskViewer", taskId!);
      });
    } else {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId: taskId, taskType: TaskType.MFAQ_FOLLOW_UP },
      });
    }
  };
  return (
    <FixaButton
      data-testid="createMFAQTask"
      type="primary"
      text={<Trans>{taskString}</Trans>}
      onClick={taskId ? showMFAQTask : createMFAQTask}
    />
  );
};

export default MfaqTaskButtonComponent;
